import type { ExprNode, UserNode } from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  type FreeTextFilter,
  isMultiFilter,
} from '../../../filters/types/filters';
import { buildOrNode, buildTxtNode, wrapExprNode } from './node-builders';
import { TalentDiscoveryV3Tags } from './types';

/**
 * Build an ExprNode from an array of text strings.
 * Trims and filters empty strings, then creates TxtNodes that are ORed together.
 * Returns null if no valid texts are found.
 */
const buildTextNodesFromStrings = (
  texts: string[],
  tag: TalentDiscoveryV3Tags,
  isCurrent?: boolean
): ExprNode | null => {
  const trimmedTexts = texts
    .map((t) => String(t).trim())
    .filter((t) => t.length > 0);

  if (trimmedTexts.length === 0) return null;

  const textNodes = trimmedTexts.map((text) =>
    buildTxtNode(tag, text, isCurrent)
  );

  if (textNodes.length === 0) return null;
  if (textNodes.length === 1) {
    return textNodes[0] ?? null;
  }

  return buildOrNode(textNodes);
};

/**
 * Build keywords filter node from filter state.
 * Handles both MULTI_KEYWORDS and single KEYWORDS filters.
 */
export const buildKeywordsFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiKeywords = findFilterById(filters, FilterId.MULTI_KEYWORDS);

  if (multiKeywords && isMultiFilter(multiKeywords)) {
    const instanceNodes = multiKeywords.instances
      .filter(
        (inst): inst is FreeTextFilter => inst?.type === FilterType.FREE_TEXT
      )
      .map((instance) => {
        const exprNode = buildTextNodesFromStrings(
          instance.text || [],
          TalentDiscoveryV3Tags.Desc
        );
        if (!exprNode) return null;

        return wrapExprNode(exprNode, instance.isNot);
      })
      .filter((node): node is UserNode => node !== null);

    if (instanceNodes.length === 0) return null;
    if (instanceNodes.length === 1) {
      const [singleNode] = instanceNodes;
      return singleNode ?? null;
    }

    // Return array of nodes when multiple instances (to avoid nested AND nodes)
    return instanceNodes;
  }

  // Fallback to single keywords filter
  const keywordsFilter = findFilterById(filters, FilterId.KEYWORDS);
  if (!keywordsFilter || keywordsFilter.type !== FilterType.FREE_TEXT) {
    return null;
  }

  const exprNode = buildTextNodesFromStrings(
    keywordsFilter.text || [],
    TalentDiscoveryV3Tags.Desc
  );
  if (!exprNode) return null;

  return wrapExprNode(exprNode, keywordsFilter.isNot);
};

/**
 * Build name filter node from filter state.
 * Name filter doesn't support MULTI variant.
 */
export const buildNameFilter = (filters: Filter[]): UserNode | null => {
  const nameFilter = findFilterById(filters, FilterId.NAME);
  if (!nameFilter || nameFilter.type !== FilterType.FREE_TEXT) {
    return null;
  }

  const nameText = nameFilter.text?.[0];
  if (!nameText) return null;

  const trimmedName = String(nameText).trim();
  if (trimmedName.length === 0) return null;

  const exprNode = buildTxtNode(TalentDiscoveryV3Tags.Name, trimmedName);

  return wrapExprNode(exprNode, nameFilter.isNot);
};

/**
 * Build titles filter node from filter state.
 * Handles both MULTI_TITLE and single TITLE filters.
 * Each title text becomes a TxtNode with isCurrent flag.
 */
export const buildTitlesFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiTitle = findFilterById(filters, FilterId.MULTI_TITLE);

  if (multiTitle && isMultiFilter(multiTitle)) {
    const instanceNodes = multiTitle.instances
      .filter(
        (inst): inst is FreeTextFilter => inst?.type === FilterType.FREE_TEXT
      )
      .map((instance) => {
        const exprNode = buildTextNodesFromStrings(
          instance.text || [],
          TalentDiscoveryV3Tags.Title,
          instance.isCurrent
        );
        if (!exprNode) return null;

        return wrapExprNode(exprNode, instance.isNot);
      })
      .filter((node): node is UserNode => node !== null);

    if (instanceNodes.length === 0) return null;
    if (instanceNodes.length === 1) {
      const [singleNode] = instanceNodes;
      return singleNode ?? null;
    }

    // Return array of nodes when multiple instances (to avoid nested AND nodes)
    return instanceNodes;
  }

  // Fallback to single title filter
  const singleTitleFilter = findFilterById(filters, FilterId.TITLE);
  if (!singleTitleFilter || singleTitleFilter.type !== FilterType.FREE_TEXT) {
    return null;
  }

  const exprNode = buildTextNodesFromStrings(
    singleTitleFilter.text || [],
    TalentDiscoveryV3Tags.Title,
    singleTitleFilter.isCurrent
  );
  if (!exprNode) return null;

  return wrapExprNode(exprNode, singleTitleFilter.isNot);
};
